"use client";

/**
 * The two app films on /why-bb/story (tour-films.ts):
 *  - TourFilm: page by page, explained — the real page in a browser frame
 *    (often the phone version beside it), what it is for, what you can do.
 *  - PulseFilm: the same pages as rhythm — slow, fast, fast, slow, slow, fast
 *    at 120 BPM: long drifting lines, then hard cuts with one word each.
 * Both play in StoryFrame (16:9, cqw units, full screen, keys).
 */

import { useEffect, useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { BBSignature } from "@/components/home/bb-signature";
import { CoverWall, FADE_MS, piSafe } from "@/components/screens/film-stage";
import { useFilmAudio, useFilmLines } from "@/components/screens/film-art";
import { MOCK_CSS, Mock } from "@/components/screens/mocks";
import { useBeatClock } from "@/components/screens/use-beat-clock";
import { cn } from "@/lib/utils";
import { StoryFrame } from "./story-frame";
import { PULSE, SHOT_PATH, SHOT_VIDEOS, TOUR, shotSrc, shotsOf, type PulseBeat, type ShotId, type TourBeat } from "./tour-films";

const FG = "#f5f1ea";

const TOUR_CSS = `
@keyframes tf-cam { from { transform: scale(1) } to { transform: scale(var(--tf-zoom, 1.08)) } }
@keyframes tf-frame-in { from { opacity: 0; transform: translateX(5cqw) rotate(1.5deg) } to { opacity: 1; transform: none } }
@keyframes tf-phone-in { from { opacity: 0; transform: translateY(8cqw) rotate(6deg) } to { opacity: 1; transform: rotate(-3deg) } }
@keyframes tf-punch { from { transform: scale(var(--tf-from)) } to { transform: scale(var(--tf-to)) } }
@keyframes tf-word { 0% { transform: scale(1.22); opacity: 0 } 25% { opacity: 1 } 100% { transform: scale(1); opacity: 1 } }
@keyframes tf-line { from { opacity: 0; letter-spacing: .28em; filter: blur(.6cqw) } to { opacity: 1; letter-spacing: .01em; filter: none } }
@keyframes tf-slow { from { transform: scale(1.04) } to { transform: scale(1.24) } }
@keyframes tf-glow { 0%,100% { opacity: .35 } 50% { opacity: .55 } }
@media (prefers-reduced-motion: reduce) { .tf-anim { animation: none !important; } }
`;

function useReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!mq) return;
    const on = () => setReduced(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return reduced;
}

/** Load and decode every picture up front, so a cut never shows an empty
 *  frame; `ready` once they all are (or after 6 s, whatever is still missing). */
function usePreload(ids: ShotId[]) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    let alive = true;
    const stills = ids.filter(id => !SHOT_VIDEOS[id]);
    const done = Promise.all(stills.map(id => {
      const img = new window.Image();
      img.src = shotSrc(id);
      return img.decode().catch(() => {});
    }));
    const timeout = new Promise(r => window.setTimeout(r, 6000));
    void Promise.race([done, timeout]).then(() => { if (alive) setReady(true); });
    return () => { alive = false; };
  }, [ids]);
  return ready;
}

/** A capture: a still, or a short screen recording (muted, looping). */
function Shot({ id, className, style, still = false }: { id: ShotId; className?: string; style?: React.CSSProperties; still?: boolean }) {
  const video = still ? undefined : SHOT_VIDEOS[id];
  if (video) {
    return <video src={video} poster={shotSrc(id)} muted autoPlay loop playsInline preload="auto" aria-hidden className={className} style={style} />;
  }
  // eslint-disable-next-line @next/next/no-img-element
  return <img src={shotSrc(id)} alt="" aria-hidden decoding="async" draggable={false} className={className} style={style} />;
}

function BrowserFrame({ id, ms, focus, delay = 0, small = false }: { id: ShotId; ms: number; focus?: string; delay?: number; small?: boolean }) {
  return (
    <div className="tf-anim overflow-hidden rounded-[0.9cqw] bg-[#0c0b0a] shadow-[0_1.6cqw_4cqw_rgba(0,0,0,0.6)] ring-1 ring-white/15"
      style={{ animation: `tf-frame-in 900ms cubic-bezier(.22,1,.36,1) ${delay}ms both` }}>
      <div className={cn("flex items-center border-b border-white/10", small ? "gap-[0.4cqw] px-[0.7cqw] py-[0.4cqw]" : "gap-[0.55cqw] px-[1cqw] py-[0.55cqw]")}>
        {[0, 1, 2].map(i => <span key={i} className={cn("shrink-0 rounded-full bg-white/25", small ? "h-[0.45cqw] w-[0.45cqw]" : "h-[0.65cqw] w-[0.65cqw]")} />)}
        <span className={cn("ml-[0.6cqw] min-w-0 flex-1 truncate rounded-full bg-white/10 font-mono text-white/70", small ? "px-[0.7cqw] py-[0.1cqw] text-[0.7cqw]" : "px-[1cqw] py-[0.2cqw] text-[0.95cqw]")}>
          breakinbread.com{SHOT_PATH[id] ?? ""}
        </span>
      </div>
      <div className="relative aspect-[16/10] overflow-hidden">
        <Shot id={id} className="tf-anim absolute inset-0 h-full w-full object-cover object-top"
          style={SHOT_VIDEOS[id] ? undefined : { transformOrigin: focus ?? "50% 30%", animation: `tf-cam ${ms}ms linear both` }} />
      </div>
    </div>
  );
}

function PhoneFrame({ id, delay = 500 }: { id: ShotId; delay?: number }) {
  return (
    <div className="tf-anim w-[12.5cqw] overflow-hidden rounded-[2cqw] bg-black p-[0.45cqw] shadow-[0_1.6cqw_4cqw_rgba(0,0,0,0.7)] ring-1 ring-white/25"
      style={{ animation: `tf-phone-in 1000ms cubic-bezier(.22,1,.36,1) ${delay}ms both` }}>
      <div className="relative aspect-[390/844] overflow-hidden rounded-[1.6cqw]">
        <Shot id={id} className="absolute inset-0 h-full w-full object-cover object-top" />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  The tour                                                            */
/* ------------------------------------------------------------------ */

function TourStage({ beat, progress, round, covers, people }: { beat: number; progress: number; round: number; covers: string[]; people: string[] }) {
  const t = useTranslations("tourFilms.tour");
  const linesFor = useFilmLines();
  const audio = useFilmAudio();
  const b: TourBeat = TOUR[beat];
  const total = useMemo(() => TOUR.reduce((s, x) => s + x.ms, 0), []);
  const before = TOUR.slice(0, beat).reduce((s, x) => s + x.ms, 0);
  const leaving = progress * b.ms > b.ms - FADE_MS;
  const shots = Array.isArray(b.shot) ? b.shot : b.shot ? [b.shot] : [];
  const pills = (b.pills ?? []).map(k => t(k));
  const headline = t(b.h);
  const visual = shots.length > 0 || !!b.mock;

  return (
    <div className="relative h-full w-full">
      {b.cover && <CoverWall covers={covers} />}
      {/* The page itself, blurred and dim, as the light in the room. */}
      {!b.cover && shots[0] && (
        <div aria-hidden key={`amb-${beat}-${round}`} className="absolute inset-0 overflow-hidden">
          <Shot id={shots[0]} still className="tf-anim absolute inset-0 h-full w-full scale-125 object-cover opacity-40 blur-[3cqw]" style={{ animation: "tf-glow 6s ease-in-out infinite" }} />
          <div className="absolute inset-0 bg-gradient-to-r from-[#171512] via-[#171512]/90 to-[#171512]/55" />
        </div>
      )}

      <div key={`${beat}-${round}`} aria-live="polite"
        className={cn("absolute inset-0 grid items-center gap-[3.5cqw] px-[6cqw] pb-[8.5cqw] pt-[7.5cqw] transition-opacity duration-700",
          visual ? "grid-cols-[minmax(0,0.85fr)_minmax(0,1.25fr)]" : "grid-cols-1", leaving ? "opacity-0" : "opacity-100")}>
        <div className="min-w-0 space-y-[1.5cqw]">
          {!b.cover && (
            <p className="bb-intro-rise font-mono text-[1.15cqw] tracking-[0.2em] opacity-60">
              {String(beat).padStart(2, "0")} / {String(TOUR.length - 2).padStart(2, "0")}
            </p>
          )}
          <p className={cn("bb-intro-rise bb-impact leading-[1.03]", b.cover ? "text-[6.2cqw]" : headline.length > 22 ? "text-[3.6cqw]" : "text-[4.4cqw]")} style={{ animationDelay: "120ms" }}>
            {piSafe(headline)}
          </p>
          {b.body && (
            <p className={cn("bb-intro-rise max-w-[40cqw] leading-snug opacity-90", b.cover ? "text-[2.2cqw]" : "text-[1.75cqw]")} style={{ animationDelay: "380ms" }}>
              {piSafe(t(b.body))}
            </p>
          )}
          {pills.length > 0 && (
            <ul className="flex flex-wrap gap-[0.7cqw]">
              {pills.map((text, i) => (
                <li key={i} className={cn("bb-intro-pop rounded-full bg-[#f5f1ea] font-semibold leading-tight text-[#171512]",
                  pills.length > 3 ? "px-[1.1cqw] py-[0.45cqw] text-[1.45cqw]" : "px-[1.3cqw] py-[0.5cqw] text-[1.65cqw]")}
                  style={{ animationDelay: `${900 + i * 420}ms` }}>
                  {piSafe(text)}
                </li>
              ))}
            </ul>
          )}
        </div>

        {visual && (
          <div className="relative min-w-0">
            {shots.length === 1 && <BrowserFrame id={shots[0]} ms={b.ms} focus={b.focus} />}
            {shots.length > 1 && (
              shots.length === 2 ? (
                // Two pages overlap, the second one in front, lower right.
                <div className="relative pb-[9cqw]">
                  <div className="w-[74%]"><BrowserFrame id={shots[0]} ms={b.ms} small /></div>
                  <div className="absolute bottom-0 right-0 w-[64%]"><BrowserFrame id={shots[1]} ms={b.ms} delay={420} small /></div>
                </div>
              ) : (
                // Four make a grid.
                <div className="grid grid-cols-2 items-start gap-[1.2cqw]">
                  {shots.map((s, i) => <BrowserFrame key={s} id={s} ms={b.ms} delay={i * 260} small />)}
                </div>
              )
            )}
            {b.phone && <div className="absolute -bottom-[2.5cqw] -right-[2cqw]"><PhoneFrame id={b.phone} /></div>}
            {b.mock && (
              <div className="flex justify-end" style={{ zoom: pills.length > 3 ? 0.85 : 0.95 }}>
                <Mock id={b.mock} covers={covers} people={people} lines={b.lines ? linesFor(b.lines) : undefined} audio={audio} seed={beat * 7 + 3} />
              </div>
            )}
          </div>
        )}
      </div>

      <p className="absolute left-[6cqw] top-[3.4cqw] text-[1.5cqw] font-bold uppercase tracking-[0.3em] opacity-70">{t("name")}</p>
      <div className="absolute inset-x-[6cqw] bottom-[2.6cqw] flex items-center gap-[2cqw]">
        <span className="text-[1.4cqw] font-semibold tracking-wide opacity-80">breakinbread.com</span>
        <span className="h-[0.25cqw] flex-1 overflow-hidden rounded-full bg-[#f5f1ea]/15">
          <span className="block h-full origin-left bg-[#f5f1ea]" style={{ transform: `scaleX(${(before + progress * b.ms) / total})` }} />
        </span>
        <BBSignature className="w-[3.4cqw] bg-[#f5f1ea]" />
      </div>
    </div>
  );
}

export function TourFilm({ name, covers, people }: { name: string; covers: string[]; people: string[] }) {
  const [playing, setPlaying] = useState(true);
  const lengths = useMemo(() => TOUR.map(x => x.ms), []);
  const ready = usePreload(useMemo(() => shotsOf("tour"), []));
  const clock = useBeatClock(lengths, { playing: playing && ready, loop: true });
  return (
    <StoryFrame label={name} playing={playing} onTogglePlay={() => setPlaying(p => !p)}
      onPrev={() => clock.goTo(clock.beat - 1)} onNext={() => clock.goTo(clock.beat + 1)} onRestart={() => { clock.goTo(0); setPlaying(true); }}>
      <style>{MOCK_CSS + TOUR_CSS}</style>
      <TourStage beat={clock.beat} progress={clock.progress} round={clock.round} covers={covers} people={people} />
    </StoryFrame>
  );
}

/* ------------------------------------------------------------------ */
/*  The pulse                                                           */
/* ------------------------------------------------------------------ */

/** A little film grain and a vignette over every frame. */
const GRAIN = "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='.55'/></svg>\")";

function PulseStage({ beat, progress, round, reduced, lengths }: { beat: number; progress: number; round: number; reduced: boolean; lengths: number[] }) {
  const t = useTranslations("tourFilms.pulse");
  const b: PulseBeat = PULSE[beat];
  const total = lengths.reduce((s, x) => s + x, 0);
  const before = lengths.slice(0, beat).reduce((s, x) => s + x, 0);
  const ms = lengths[beat] ?? b.ms;
  // Every other fast cut in black and white — the artwork keeps its colour on the rest.
  const mono = b.kind === "fast" && beat % 2 === 1;
  const zoom = b.zoom ?? 1;
  const slowLeaving = b.kind !== "fast" && progress * ms > ms - 900;
  const phone = b.shot.startsWith("p-");

  return (
    <div className="relative h-full w-full overflow-hidden bg-[#0b0a09]">
      <div key={`${beat}-${round}`} className="absolute inset-0">
        {/* The picture */}
        <div className={cn("absolute inset-0 transition-opacity duration-[900ms]", slowLeaving ? "opacity-0" : "opacity-100")}>
          {phone && b.kind !== "fast" ? (
            // A phone page stands upright in the middle, its own blur behind it.
            <>
              <Shot id={b.shot} still className="absolute inset-0 h-full w-full scale-125 object-cover opacity-35 blur-[2.5cqw]" />
              <div className="absolute inset-y-[6%] left-1/2 aspect-[390/844] -translate-x-1/2">
                <div className="tf-anim h-full w-full overflow-hidden rounded-[2cqw] ring-1 ring-white/20" style={{ animation: `tf-slow ${ms}ms ease-out both` }}>
                  <Shot id={b.shot} className="h-full w-full object-cover object-top" />
                </div>
              </div>
            </>
          ) : (
            // Fast cuts use stills (a recording can't start in half a second) and crop on their own spot.
            <Shot id={b.shot} still={b.kind === "fast"}
              className={cn("tf-anim absolute inset-0 h-full w-full object-cover", phone && b.kind !== "fast" ? "object-top" : "")}
              style={b.kind === "fast"
                ? { transformOrigin: b.at ?? "50% 50%", objectPosition: b.at ?? "50% 50%", ["--tf-from" as string]: String(zoom * 1.14), ["--tf-to" as string]: String(zoom), transform: `scale(${zoom})`,
                    animation: reduced ? undefined : "tf-punch 320ms cubic-bezier(.2,1,.3,1) both",
                    filter: mono ? "grayscale(1) contrast(1.35) brightness(.8)" : "contrast(1.12) saturate(1.15) brightness(.78)" }
                : { animation: `tf-slow ${ms}ms ease-out both`, filter: "saturate(.85) brightness(.62)" }} />
          )}
          <div className={cn("absolute inset-0", b.kind === "fast" ? "bg-[#0b0a09]/25" : "bg-gradient-to-tr from-[#0b0a09] via-[#0b0a09]/55 to-transparent")} />
        </div>

        {/* The words */}
        {b.kind === "slow" && b.w && (
          <p className={cn("tf-anim absolute bottom-[12cqw] left-[6cqw] max-w-[62cqw] text-[4.4cqw] font-light leading-[1.12] transition-opacity duration-[900ms]", slowLeaving ? "opacity-0" : "opacity-100")}
            style={{ animation: "tf-line 2200ms cubic-bezier(.22,1,.36,1) 300ms both" }}>
            {piSafe(t(b.w))}
          </p>
        )}
        {b.kind === "fast" && b.w && (
          <p className="tf-anim bb-impact absolute inset-0 grid place-items-center px-[4cqw] text-center text-[14cqw] uppercase leading-none"
            style={{
              animation: reduced ? undefined : "tf-word 300ms cubic-bezier(.2,1,.3,1) both",
              ...(b.outline ? { color: "transparent", WebkitTextStroke: `0.28cqw ${FG}` } : { color: FG, textShadow: "0 0.6cqw 3cqw rgba(0,0,0,.6)" }),
            }}>
            {piSafe(t(b.w))}
          </p>
        )}
        {b.kind === "title" && (
          <div className="absolute inset-0 grid place-items-center">
            <div className="flex flex-col items-center gap-[2cqw] text-center">
              <BBSignature className="tf-anim w-[16cqw] bg-[#f5f1ea]" />
              <p className="tf-anim bb-impact text-[6cqw] uppercase leading-none" style={{ animation: "tf-line 2000ms cubic-bezier(.22,1,.36,1) 400ms both" }}>Breakin&apos; Bread</p>
              <p className="tf-anim text-[2.2cqw] font-light opacity-90" style={{ animation: "tf-line 2000ms cubic-bezier(.22,1,.36,1) 1100ms both" }}>{piSafe(t(b.w ?? "end"))}</p>
              <p className="tf-anim font-mono text-[1.6cqw] tracking-[0.25em] opacity-70" style={{ animation: "tf-line 1800ms cubic-bezier(.22,1,.36,1) 1800ms both" }}>BREAKINBREAD.COM</p>
            </div>
          </div>
        )}
      </div>

      {/* The next two pictures, drawn invisibly so each cut is ready to paint. */}
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.001]">
        {[1, 2].map(k => {
          const next = PULSE[(beat + k) % PULSE.length];
          return <Shot key={k} id={next.shot} still className="absolute inset-0 h-full w-full object-cover" />;
        })}
      </div>

      {/* Grain + vignette, the same on every frame */}
      <div aria-hidden className="pointer-events-none absolute inset-0 opacity-[0.13] mix-blend-overlay" style={{ backgroundImage: GRAIN }} />
      <div aria-hidden className="pointer-events-none absolute inset-0 shadow-[inset_0_0_14cqw_rgba(0,0,0,0.75)]" />

      <p className="absolute left-[6cqw] top-[3.4cqw] text-[1.5cqw] font-bold uppercase tracking-[0.3em] opacity-70">{t("name")}</p>
      <div className="absolute inset-x-[6cqw] bottom-[2.6cqw] flex items-center gap-[2cqw]">
        <span className="h-[0.2cqw] flex-1 overflow-hidden rounded-full bg-[#f5f1ea]/15">
          <span className="block h-full origin-left bg-[#f5f1ea]" style={{ transform: `scaleX(${(before + progress * ms) / total})` }} />
        </span>
        <BBSignature className="w-[3cqw] bg-[#f5f1ea]" />
      </div>
    </div>
  );
}

export function PulseFilm({ name }: { name: string }) {
  const [playing, setPlaying] = useState(true);
  const reduced = useReducedMotion();
  // Reduced motion: every cut lasts a second, nothing punches in.
  const lengths = useMemo(() => PULSE.map(x => (reduced && x.kind === "fast" ? 1000 : x.ms)), [reduced]);
  // The cuts only start once every picture is decoded.
  const ready = usePreload(useMemo(() => shotsOf("pulse"), []));
  const clock = useBeatClock(lengths, { playing: playing && ready, loop: true, stepMs: 25 });
  return (
    <StoryFrame label={name} playing={playing} onTogglePlay={() => setPlaying(p => !p)}
      onPrev={() => clock.goTo(clock.beat - 1)} onNext={() => clock.goTo(clock.beat + 1)} onRestart={() => { clock.goTo(0); setPlaying(true); }}>
      <style>{TOUR_CSS}</style>
      <PulseStage beat={clock.beat} progress={clock.progress} round={clock.round} reduced={reduced} lengths={lengths} />
    </StoryFrame>
  );
}
